// =============================================================================
// features/crm/components/LeadDocumentsTab.tsx — Aba "Documentos" na ficha do
// lead (F46-S09).
//
// Central de documentos do cidadão: agrupa TUDO (análise, contrato, WhatsApp,
// upload avulso) num único painel — o cidadão não deve se importar de onde
// veio o documento. Renderiza `<DocumentsPanel scope="lead" />` (F46-S08) sem
// duplicar lógica de listagem/download/gate — a superfície pública de
// features/documents é a única fonte de verdade (ver features/documents/index.ts).
//
// Gate: `useDocumentsGate()` decide se a aba existe. Com a flag
// `documents.enabled` desligada ou sem `documents:read`, a aba não renderiza
// NADA — nem a moldura do card — para não deixar um retângulo vazio na ficha
// (nota do slot: "não adicione hook de documento fora do gate").
//
// DS: mesmo padrão de card Spotlight (halo verde) das demais seções da ficha
// (ver LeadCreditAnalysisTab.tsx) — não existe um `SpotlightCard` compartilhado
// em components/ui ainda, cada seção define o seu localmente.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../lib/cn';
import { DocumentsPanel, useDocumentsGate, type DocumentsGate } from '../../documents';

// ─── Helper puro (testado em __tests__/LeadDocumentsTab.test.tsx) ──────────

/**
 * Decide se a aba deve renderizar algo. Enquanto a flag ainda está
 * carregando, a aba renderiza a moldura + skeleton interno do
 * `DocumentsPanel`, exatamente como qualquer outro estado de loading da
 * ficha — só depois de resolvido é que ela pode sumir de verdade.
 */
export function shouldRenderDocumentsTab(
  gate: Pick<DocumentsGate, 'isLoading' | 'canRead'>,
): boolean {
  if (gate.isLoading) return true;
  return gate.canRead;
}

// ─── Spotlight Card (mesmo padrão local de LeadCreditAnalysisTab.tsx) ──────

function SpotlightCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}): React.JSX.Element {
  const cardRef = React.useRef<HTMLDivElement>(null);

  const handleMouseMove = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty('--mx', `${e.clientX - rect.left}px`);
    el.style.setProperty('--my', `${e.clientY - rect.top}px`);
  }, []);

  const handleMouseLeave = React.useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty('--mx', '-9999px');
    el.style.setProperty('--my', '-9999px');
  }, []);

  return (
    <div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'relative overflow-hidden rounded-md border border-border bg-surface-1',
        'transition-[transform,box-shadow] duration-[250ms] ease-out',
        'hover:-translate-y-0.5',
        '[--mx:-9999px] [--my:-9999px]',
        className,
      )}
      style={{ boxShadow: 'var(--elev-2)' }}
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 rounded-md"
        style={{
          background:
            'radial-gradient(300px circle at var(--mx) var(--my), rgba(46,155,62,0.06), transparent 60%)',
        }}
      />
      <div className="relative z-10">{children}</div>
    </div>
  );
}

// ─── Componente ──────────────────────────────────────────────────────────────

export interface LeadDocumentsTabProps {
  leadId: string;
}

/**
 * Aba "Documentos" da ficha do lead. Sem rol obrigatório (quem tem checklist
 * é a análise, não a ficha — fora de escopo deste slot).
 */
export function LeadDocumentsTab({ leadId }: LeadDocumentsTabProps): React.JSX.Element | null {
  const gate = useDocumentsGate();

  if (!shouldRenderDocumentsTab(gate)) return null;

  return (
    <SpotlightCard>
      <div className="p-5">
        <DocumentsPanel leadId={leadId} scope="lead" />
      </div>
    </SpotlightCard>
  );
}
